// React & Hooks
import { useState, useEffect, useContext } from "react";
import moment from "moment";

// MUI Components
import {
  Grid,
  Box,
  Table,
  TableRow,
  TableCell,
  TableBody,
  TableHead,
  Typography,
  TableContainer,
  Button,
} from "@mui/material";

// Styles
import "../../../Style/pagestyle.scss";

// Context
import { AuthContext } from "../../../Context/AuthContext";

// Utils & Shared Components
import LoadingPage from "../../../Utils/LoadingPage";
import EmptyData from "../../Include/EmptyData/EmptyData";
import FooterPagination from "../../../Utils/FooterPagination";
import { ButtonCreate, SearchTextField } from "../../../Utils/Component";

// Feature Components
import TeachingTimeForm from "./TeachingTimeForm";
import TeachingTimeAction from "./TeachingTimeAction";

// Dynamic Filters
import {
  SelectStudentByClassForTeachingTime,
  SelectSubjectByClassId,
} from "../../Include/DynamicSelect/DynamicSelect";
// import { SelectDatePicker } from "../../Include/SelectDatePicker/SelectDatePicker";

// Data Hook
import useGetTeachingTimesPagination from "../../../Hooks/useGetTeachingTimesPagination";
import useWithDimension from "../../../Hooks/useWithDimension";
import { SelectDatePicker } from "../../Include/SelectDatePicker/SelectDatePicker";

export default function TeachingTime({ classId }) {
  const { language, t } = useContext(AuthContext);
  const { width } = useWithDimension();
  const [expandedRows, setExpandedRows] = useState({});

  const toggleRowExpansion = (rowId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [rowId]: !prev[rowId],
    }));
  };

  // Filters state
  // const [toDate, setToDate] = useState(moment());
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  // const [fromDate, setFromDate] = useState(moment().startOf("month"));
  const [subject, setSubject] = useState({ id: "", title: "" });
  const [studentVal, setStudentVal] = useState({
    id: "",
    title: "",
  });

  // Table and pagination data
  const {
    page,
    limit,
    loading,
    refetch,
    setPage,
    tableData,
    setLoading,
    handleLimit,
    handleSearch,
    paginationData,
  } = useGetTeachingTimesPagination({
    toDate: moment(toDate).format("YYYY-MM-DD") || "",
    fromDate: moment(fromDate).format("YYYY-MM-DD") || "",
    classId: classId || "",
    subjectId: subject?.id || "",
    studentId: studentVal?.id || "",
  });

  // console.log("paginationData::", paginationData, tableData);

  // Create form modal
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  return (
    <div>
      {/* ================== Filters ================== */}
      <Box sx={{ marginTop: "10px" }}>
        <Grid container spacing={3}>
          {/* Search Input */}
          <Grid item xs={6} sm={4} md={3} lg={2.4} xl={2}>
            {/* <Typography className={t("class-name-text-field-title")}>
              {t("search")}
            </Typography> */}
            {/* <SearchTextField
              className="text-field"
              handleSearch={handleSearch}
            /> */}
            <Typography className={t("class-name-text-field-title")}>
              {t("thead_student")}
            </Typography>
            <SelectStudentByClassForTeachingTime
              className="text-field"
              onChange={setStudentVal}
              value={studentVal}
              classId={classId}
            />
          </Grid>

          {/* Subject Filter */}
          <Grid item xs={6} sm={4} md={3} lg={2.4} xl={2}>
            <Typography className={t("class-name-text-field-title")}>
              {t("thead_subject")}
            </Typography>
            <SelectSubjectByClassId
              value={subject}
              classId={classId}
              onChange={setSubject}
              className="text-field"
            />
          </Grid>

          {/* From Date */}
          <Grid item xs={6} sm={4} md={3} lg={2.4} xl={2}>
            <Typography className={t("class-name-text-field-title")}>
              {t("thead_start_date")}
            </Typography>
            <SelectDatePicker value={fromDate} onChange={setFromDate} />
          </Grid>

          {/* To Date */}
          <Grid item xs={6} sm={4} md={3} lg={2.4} xl={2}>
            <Typography className={t("class-name-text-field-title")}>
              {t("thead_end_date")}
            </Typography>
            <SelectDatePicker value={toDate} onChange={setToDate} />
          </Grid>

          {/* Create Button */}
          <Grid item xs={12} sm={8} md={12} lg={2.4} xl={4}>
            <ButtonCreate
              handleOpen={handleOpen}
              classBtn="btn-create"
              classIcon="icon-add"
            />

            {open && (
              <TeachingTimeForm
                open={open}
                classId={classId}
                handleClose={handleClose}
                setRefetch={refetch}
                dialogTitle="Create"
              />
            )}
          </Grid>
        </Grid>
      </Box>

      {/* ================== Table ================== */}
      <Box className="body-container">
        <TableContainer sx={{ maxWidth: `${width}px` }}>
          <Table className="table" aria-label="teaching-time-table">
            <TableHead className="header-row">
              <TableRow>
                <TableCell
                  className={t("class-name-header-title-start")}
                  width="5%"
                >
                  {t("no")}
                </TableCell>
                <TableCell className={t("class-name-header-title")} width="15%">
                  {t("thead_name")}
                </TableCell>
                <TableCell className={t("class-name-header-title")} width="10%">
                  {t("thead_subject")}
                </TableCell>
                <TableCell className={t("class-name-header-title")} width="10%">
                  {t("thead_start_date")}
                </TableCell>
                <TableCell className={t("class-name-header-title")} width="10%">
                  {t("thead_end_date")}
                </TableCell>
                <TableCell className={t("class-name-header-title")} width="25%">
                  {t("thead_lesson")}
                </TableCell>

                {/* <TableCell
                  className={t("class-name-header-title")}
                  width="20%"
                  align="center"
                >
                  {t("thead_time")}
                </TableCell> */}

                <TableCell className={t("class-name-header-title")} width="20%">
                  {t("thead_remark")}
                </TableCell>
                <TableCell
                  className={t("class-name-header-title-end")}
                  width="5%"
                />
              </TableRow>
            </TableHead>

            <TableBody className="body">
              {loading ? (
                <TableRow className="body-row">
                  <TableCell
                    colSpan={8}
                    className="body-cell"
                    sx={{ bgcolor: "#fff" }}
                  >
                    <LoadingPage />
                  </TableCell>
                </TableRow>
              ) : tableData?.length === 0 || tableData?.length === undefined ? (
                <TableRow className="body-row">
                  <TableCell colSpan={8} className="body-cell">
                    <EmptyData />
                  </TableCell>
                </TableRow>
              ) : (
                tableData?.map((row, index) => {
                  // console.log("row::", row);
                  const isExpanded = expandedRows[row?._id] || false;
                  return (
                    <TableRow className="body-row" key={row._id || index}>
                      <TableCell className="body-cell-start">
                        {index + 1}
                      </TableCell>
                      <TableCell className="body-cell">
                        {row?.studentId?.familyName +
                          " " +
                          row?.studentId?.studentName}
                      </TableCell>
                      <TableCell className="body-cell">
                        {language === "en"
                          ? row?.subjectId?.enName
                          : row?.subjectId?.khName}
                      </TableCell>
                      <TableCell className="body-cell">
                        {row?.fromDate &&
                          moment(row?.fromDate).format("DD MMM YYYY")}
                      </TableCell>
                      <TableCell className="body-cell">
                        {row?.toDate &&
                          moment(row?.toDate).format("DD MMM YYYY")}
                      </TableCell>
                      <TableCell
                        className="body-cell"
                        style={{ whiteSpace: "pre-line" }}
                      >
                        {/* {row?.lesson} */}
                        {isExpanded ? row?.lesson : row?.lesson?.slice(0, 100)}
                        {row?.lesson?.length > 100 && (
                          <Button
                            onClick={() => toggleRowExpansion(row?._id)}
                            size="small"
                            className="see-more"
                          >
                            {isExpanded ? "See less" : "...See more"}
                          </Button>
                        )}
                      </TableCell>
                      {/* <TableCell className="body-cell" align="center">
                      {row?.startTime} - {row?.endTime}
                    </TableCell> */}
                      <TableCell className="body-cell">{row?.remark}</TableCell>
                      <TableCell className="body-cell-end" align="right">
                        <TeachingTimeAction
                          editData={row}
                          classId={classId}
                          setRefetch={refetch}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* ================== Pagination ================== */}
      <FooterPagination
        totalPages={paginationData?.totalPages}
        totalDocs={paginationData?.totalDocs}
        limit={limit}
        page={page}
        setPage={setPage}
        handleLimit={handleLimit}
        setLoading={setLoading}
      />
    </div>
  );
}
